import React from 'react';
import { Tag, Typography, theme } from 'antd';
import { IconPhoto } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { IAccessorySearchResult } from '../../interfaces/heavyDutyMachinery/IMachineryAccessory';
import { useTaxonomy } from '../../hooks/useTaxonomy';
import { useAccessoryTaxonomy } from '../../hooks/useAccessoryTaxonomy';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { formatCurrency } from '../../helpers/currencyHelper';

const { Title, Text } = Typography;

const MAX_VISIBLE_CATEGORIES = 3;

interface AccessorySearchCardProps {
  accessory: IAccessorySearchResult;
  onClick?: () => void;
  /** Minimal photo+title+price rendering for tight spaces (the map's pin-click popup) -- same role as HeavyDutyMachineryCard's own compact prop. */
  compact?: boolean;
}

/** Guest-facing map/result card for a MachineryAccessory -- same visual language (photo block, rounded 16px/12px, title+price layout) as HeavyDutyMachineryCard/OperatorSearchCard. */
const AccessorySearchCard: React.FC<AccessorySearchCardProps> = ({ accessory, onClick, compact }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const { resolveName: categoryLabel } = useTaxonomy();
  const { resolveName: accessoryTypeLabel } = useAccessoryTaxonomy();

  const categories = accessory.compatibleCategories.split(',').map((c) => c.trim()).filter(Boolean);
  const photoUrl = accessory.photoUrls[0];
  const priceLabel = `${formatCurrency(accessory.pricePerDay, 'EUR', i18n.language)} ${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_UNIT_DAY_PASS)}`;

  const photoBox = (radius: number, iconSize: number) =>
    photoUrl ? (
      <img src={photoUrl} alt={accessory.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: radius }} />
    ) : (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: radius,
          background: token.colorFillTertiary,
          color: token.colorTextQuaternary,
        }}
      >
        <IconPhoto size={iconSize} />
      </div>
    );

  if (compact) {
    return (
      <div onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default', width: 200 }}>
        <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 10', overflow: 'hidden', borderRadius: 12 }}>{photoBox(12, 24)}</div>
        <div style={{ paddingTop: 8 }}>
          <Text strong style={{ display: 'block', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {accessory.name}
          </Text>
          <Text style={{ fontSize: 13 }}>{priceLabel}</Text>
        </div>
      </div>
    );
  }

  return (
    <div onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 10', overflow: 'hidden', borderRadius: 16 }}>{photoBox(16, 32)}</div>

      <div style={{ paddingTop: 10, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Title level={5} style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {accessory.name}
        </Title>

        {accessory.accessoryTypeCode && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {accessoryTypeLabel(accessory.accessoryTypeCode)}
          </Text>
        )}

        <Text type="secondary" style={{ fontSize: 13 }}>
          {accessory.machineryTitle}
        </Text>

        {accessory.description && (
          <Text
            type="secondary"
            style={{ fontSize: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {accessory.description}
          </Text>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
          {categories.slice(0, MAX_VISIBLE_CATEGORIES).map((category) => (
            <Tag key={category} style={{ margin: 0, borderRadius: 999 }}>
              {categoryLabel(category)}
            </Tag>
          ))}
          {categories.length > MAX_VISIBLE_CATEGORIES && (
            <Tag style={{ margin: 0, borderRadius: 999 }}>+{categories.length - MAX_VISIBLE_CATEGORIES}</Tag>
          )}
        </div>

        <div style={{ marginTop: 'auto', paddingTop: 8 }}>
          <Text strong style={{ fontSize: 15 }}>
            {priceLabel}
          </Text>
        </div>
      </div>
    </div>
  );
};

export default AccessorySearchCard;
